import { ReactNode } from 'react';
import { IoIosCheckmarkCircle } from 'react-icons/io';
import { IoMdCheckmark } from 'react-icons/io';

import { timeAgo } from '@core/util/util.timeAgo';
import { Button } from '@frontend/components/ui/button';
import { Directory } from '@frontend/shared/components/Directory/Directory';
import { useDeleter } from '@frontend/shared/components/Question/hooks/Question.hooks.useDeleter';
import { QuestionContext } from '@frontend/shared/components/Question/QuestionContext';
import { useIsEditor } from '@frontend/shared/hooks/shared.hook.useIsEditor';
import { useDirectoryCategory } from '@frontend/shared/hooks/useDirectory';
import { useRequiredContext } from '@frontend/shared/hooks/useRequiredContext';

export function QuestionVerifiedBar({ onEdit }: { onEdit: () => void }) {
  const question = useRequiredContext(QuestionContext);
  const isEditor = useIsEditor();
  const category = useDirectoryCategory(question.category_id);

  const { isDeleting, onConfirmDelete, confirmer } = useDeleter(question.id);

  let veriText: ReactNode = (
    <>
      Verified by <Directory userId={question.verified_by} />
      {` — ${timeAgo(question.verified_at ?? '')}`}
    </>
  );
  let veriBg = 'rgba(229, 252, 235, 1)';
  let veriWeight = 400;
  const isUnverified = question.verified_by === 'UNVERIFIED';
  if (isUnverified) {
    veriWeight = 700;
    const sourceUrl = question.source_url;
    veriText = (
      <>
        Unverified
        {sourceUrl ? (
          <>
            &nbsp;→&nbsp;
            <Button
              asChild
              variant="link"
              size="sm"
              className="font-bold text-[#10b981]"
            >
              <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
                Slack Conversation
              </a>
            </Button>
          </>
        ) : (
          ''
        )}
      </>
    );
    veriBg = 'rgba(248, 251, 250, 0.90)';
  }

  return (
    <div
      className="flex text-[#3A3A3A] font-medium items-center px-5 py-2 min-h-16 rounded-b-lg"
      style={{ background: veriBg }}
    >
      {!isUnverified && (
        <IoIosCheckmarkCircle className="text-[rgba(3,52,38,1)] w-6 h-6" />
      )}
      <p
        className="ml-2 text-[13px] leading-4"
        style={{
          color: isUnverified ? 'rgba(3, 52, 38, 0.50)' : undefined,
          fontWeight: veriWeight,
        }}
      >
        {veriText}
      </p>
      {category && !isUnverified && (
        <p className="ml-auto mr-3 text-[rgba(3,52,38,0.7)] font-medium text-[13px] leading-4">
          {category.name}
        </p>
      )}
      {isUnverified && isEditor && (
        <>
          <Button
            className="ml-auto font-semibold text-[13px] leading-4 px-5"
            size="sm"
            disabled={isDeleting}
            onClick={onConfirmDelete}
            variant="destructive"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
          <Button
            className="ml-3 font-semibold text-[13px] leading-4 px-4 text-[#033426] bg-[#72F57F] hover:bg-[#58D95E]"
            onClick={onEdit}
            size="sm"
          >
            <IoMdCheckmark className="mr-2 h-4 w-4" />
            Verify
          </Button>

          {confirmer}
        </>
      )}
    </div>
  );
}
